import { c as _c } from "react/compiler-runtime";
import type { AgentSuggestionOp } from '@onefootprint/request-types/dashboard';
import { useBuilder } from './provider';
import { useNormsSuggestions, useSkillsSuggestions } from './suggestions';
import { useOrderedCanvasSuggestions } from './suggestions-nav';
import type { BundleNode, Suggestion } from './types';
export type SuggestionView = 'plan' | 'norms' | 'skills';
export const viewForOp = (op: AgentSuggestionOp): SuggestionView => {
  if (op.kind === 'update_norms') return 'norms';
  if (op.kind === 'update_skills') return 'skills';
  return 'plan';
};
export type SuggestionSectionKind = 'inputs' | 'outputs' | 'node' | 'newStep' | 'norms' | 'skills';
export type SuggestionSection = {
  key: string;
  kind: SuggestionSectionKind;
  node?: BundleNode;
  items: Suggestion[];
};
const nodeIdOf = (op: AgentSuggestionOp): string | undefined => {
  if (op.kind === 'update_node') return op.node.id;
  if ('nodeId' in op) return op.nodeId;
  return undefined;
};
type Descriptor = Omit<SuggestionSection, 'items'>;
const descriptorFor = (op: AgentSuggestionOp, nodesById: Map<string, BundleNode>): Descriptor => {
  if (op.kind === 'update_inputs') return {
    key: 'inputs',
    kind: 'inputs'
  };
  if (op.kind === 'update_outputs') return {
    key: 'outputs',
    kind: 'outputs'
  };
  if (op.kind === 'update_norms') return {
    key: 'norms',
    kind: 'norms'
  };
  if (op.kind === 'update_skills') return {
    key: 'skills',
    kind: 'skills'
  };
  if (op.kind === 'add_node') return {
    key: `add:${op.parentNodeId ?? 'head'}`,
    kind: 'newStep'
  };
  const nodeId = nodeIdOf(op);
  return {
    key: nodeId ?? 'unknown',
    kind: 'node',
    node: nodeId ? nodesById.get(nodeId) : undefined
  };
};

// Groups the ordered list into consecutive same-surface sections; a different key opens a new
// section, so two add-node groups at separate gaps stay distinct.
export const buildSuggestionSections = (ordered: Suggestion[], nodes: BundleNode[]): SuggestionSection[] => {
  const nodesById = new Map(nodes.map(n => [n.id, n]));
  const sections: SuggestionSection[] = [];
  for (const s of ordered) {
    const descriptor = descriptorFor(s.operation, nodesById);
    const last = sections[sections.length - 1];
    if (last && last.key === descriptor.key && last.kind === descriptor.kind) {
      last.items.push(s);
    } else {
      sections.push({
        ...descriptor,
        items: [s]
      });
    }
  }
  return sections;
};
export const useAllOrderedSuggestions = () => {
  const $ = _c(4);
  const canvas = useOrderedCanvasSuggestions();
  const norms = useNormsSuggestions();
  const skills = useSkillsSuggestions();
  let t0;
  if ($[0] !== canvas || $[1] !== norms || $[2] !== skills) {
    t0 = [...canvas, ...norms, ...skills];
    $[0] = canvas;
    $[1] = norms;
    $[2] = skills;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  return t0;
};
export const useSuggestionSections = () => {
  const $ = _c(3);
  const ordered = useAllOrderedSuggestions();
  const nodes = useBuilder().state.bundle.nodes;
  let t0;
  if ($[0] !== nodes || $[1] !== ordered) {
    t0 = buildSuggestionSections(ordered, nodes);
    $[0] = nodes;
    $[1] = ordered;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  return t0;
};